import React, { useState, useEffect, useRef } from 'react';
import { 
  Mic, MicOff, Play, Pause, Square, Upload, FileAudio, 
  Users, UserCheck, Clock, Sparkles, Download, Copy, Check, 
  ArrowLeft, Home, Edit2, RefreshCw, AlertCircle, ShieldCheck, 
  FileText, Layers, CheckCircle2, ChevronRight, X, Volume2, VolumeX,
  FileCode, Send, HelpCircle
} from 'lucide-react';
import { autoDownloadReport } from '../utils/autoDownloader';

interface MeetingTranscriberViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  fromCenter?: 'business' | 'office' | 'admin';
  currentUserEmail?: string;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

interface SpeakerMapping {
  original: string; // e.g. "Спікер 1" / "Speaker 1"
  custom: string;   // e.g. "Олександр (CFO)"
}

const DEMO_TRANSCRIPT_UK = `# 📋 ПРОТОКОЛ НАРАДИ ТА СТЕНОГРАМА ПЕРЕГОВОРІВ

## 1. Загальні відомості та Резюме Наради (Executive Summary)
* **Дата та час проведення:** 28 серпня 2026 року, 11:30
* **Тема зустрічі:** Стратегічні переговори щодо впровадження B2B автоматизації та енергозбереження
* **Тривалість запису:** 00:08:45
* **Виявлено унікальних мовців:** 3 спікери
* **Підсумковий статус:** Угода узгоджена у першому читанні, сформовано перелік доручень
* **ШІ-Модель розпізнавання:** Gemini 3.5 Transcribe (Live & Diarization)

### 📌 Коротке резюме (Executive Summary)
Під час зустрічі сторони обговорили умови контракту на поставку та впровадження енергозберігаючого обладнання й автоматизації B2B воронки. [Спікер 1] презентував розрахунок ROI та гарантії 24/7. [Спікер 2] висловила застереження щодо строків окупності та запитала відстрочку платежу на 30 днів. [Спікер 3] підтвердив технічну готовність інфраструктури та погодив інтеграцію через API протягом 10 днів. Учасники досягли згоди щодо надання товарного кредиту із гарантійним депозитом 20%.

---

## 2. Матриця Рішень та Доручень (Action Items & Decisions)

| № | Рішення / Доручення | Відповідальний | Термін (Дедлайн) | Статус |
|---|--------------------|----------------|------------------|--------|
| 1 | Підготувати нову редакцію Договору з відстрочкою 30 днів та депозитом 20% | [Спікер 1] | 02.09.2026 | 🟡 В роботі |
| 2 | Надати технічні специфікації серверного обладнання для API-інтеграції | [Спікер 3] | 03.09.2026 | 🟡 В роботі |
| 3 | Узгодити фінальну кошторисну сітку з фінансовим департаментом | [Спікер 2] | 04.09.2026 | ⏳ Очікує |
| 4 | Провести підписання контракту та акта прийому-передачі КЕП | Усі учасники | 07.09.2026 | ⏳ Планується |

---

## 3. Аналіз Заперечень та Ризиків Переговорів
* **Заперечення 1 (Окупність):** Замовник висловив сумнів щодо 2-річного періоду окупності. *Рішення:* Надано розрахунок компенсації втрат Net Billing.
* **Заперечення 2 (Капітальні витрати):** Запитувалась знижка 10%. *Рішення:* Замість знижки узгоджено відстрочку платежу на 30 днів без відсотків.
* **Технічний ризик:** Можлива затримка налаштування WMS/ERP. *Рішення:* Закріплено персонального інженера інтеграції.

---

## 4. Повна Хронологічна Стенограма Переговорів (Verbatim Transcript)

* **[00:00:05] [Спікер 1]:** Доброго дня, колеги! Вітаю всіх на підсумкових переговорах. Сьогодні маємо погодити фінальні умови контракту та терміни впровадження.
* **[00:00:18] [Спікер 2]:** Вітаю! Ми детально вивчили вашу комерційну пропозицію. В цілому модель нас влаштовує, але є критичне питання щодо строків окупності та форми розрахунків.
* **[00:00:35] [Спікер 1]:** Зрозуміло. Щодо окупності: за нашими розрахунками економічний ефект у 1.5 млн грн/рік досягається вже на 14-й місяць за рахунок оптимізації питомих витрат.
* **[00:01:02] [Спікер 3]:** З технічної точки зору наш IT-відділ готовий до інтеграції. Система АСКОЕ та CRM можуть бути підключені через REST API буквально за 10 робочих днів.
* **[00:01:25] [Спікер 2]:** Це хороші терміни. Проте ми просимо передбачити відстрочку платежу на 30 днів після поставки першої партії обладнання.
* **[00:01:48] [Спікер 1]:** Ми можемо піти назустріч і надати 30 днів відстрочки за умови внесення 20% гарантійного депозиту при підписанні специфікації.
* **[00:02:10] [Спікер 2]:** Така формула нам підходить. Домовилися! Прошу зафіксувати це в протоколі та підготувати проєкт договору на понеділок.
* **[00:02:28] [Спікер 3]:** До понеділка я також надішлю специфікації серверів для підключення аналітичного модуля.
* **[00:02:40] [Спікер 1]:** Відмінно. Дякую всім за конструктивну роботу, протокол наради буде надіслано на пошти.`;

const DEMO_TRANSCRIPT_PL = `# 📋 PROTOKÓŁ SPOTKANIA I TRANSKRYPCJA NEGOCJACJI

## 1. Informacje Ogólne i Podsumowanie Spotkania (Executive Summary)
* **Data i godzina:** 28 sierpnia 2026 r., godz. 11:30
* **Temat spotkania:** Negocjacje strategiczne wdrożenia automatyzacji B2B oraz efektywności energetycznej
* **Czas trwania nagrania:** 00:08:45
* **Wykryto unikalnych mówców:** 3 osoby
* **Status końcowy:** Porozumienie wstępne osiągnięte, sporządzono wykaz zadań i terminów
* **Model AI Transkrypcji:** Gemini 3.5 Transcribe (Live & Diarization)

### 📌 Podsumowanie Wykonawcze (Executive Summary)
Podczas spotkania omówiono warunki umowy dostawy i wdrożenia systemów oszczędzania energii oraz automatyzacji procesów B2B. [Mówca 1] przedstawił kalkulację ROI i gwarancję wsparcia 24/7. [Mówca 2] zgłosiła zastrzeżenia co do czasu zwrotu z inwestycji i wnioskowała o 30-dniowy termin płatności. [Mówca 3] potwierdził gotowość infrastruktury IT do integracji API w ciągu 10 dni roboczych. Strony ustaliły odroczenie płatności z kaucją gwarancyjną w wysokości 20%.

---

## 2. Macierz Decyzji i Zadań (Action Items & Decisions)

| Lp. | Decyzja / Zadanie | Osoba odpowiedzialna | Termin (Deadline) | Status |
|-----|-------------------|----------------------|-------------------|--------|
| 1 | Przygotowanie zaktualizowanego projektu Umowy z 30-dniowym terminem i 20% kaucją | [Mówca 1] | 02.09.2026 | 🟡 W toku |
| 2 | Przekazanie specyfikacji technicznej serwerów pod integrację API | [Mówca 3] | 03.09.2026 | 🟡 W toku |
| 3 | Zatwierdzenie finalnego kosztorysu z Działem Finansowym | [Mówca 2] | 04.09.2026 | ⏳ Oczekuje |
| 4 | Podpisanie umowy i protokołu kwalifikowanym podpisem elektronicznym | Wszyscy uczestnicy | 07.09.2026 | ⏳ Zaplanowano |

---

## 3. Analiza Zastrzeżeń i Ryzyk Negocjacyjnych
* **Zastrzeżenie 1 (Zwrot z inwestycji):** Klient wyraził obawy o 2-letni okres zwrotu. *Rozwiązanie:* Przedstawiono audyt oszczędności Net-Billing.
* **Zastrzeżenie 2 (Płatności):** Wnioskowano o rabat 10%. *Rozwiązanie:* Zamiast rabatu udzielono 30 dni odroczenia płatności bez odsetek.
* **Ryzyko techniczne:** Możliwe opóźnienie konfiguracji WMS/ERP. *Rozwiązanie:* Dedykowany inżynier wdrożeniowy po stronie dostawcy.

---

## 4. Pełna Chronologiczna Transkrypcja Rozmowy (Verbatim Transcript)

* **[00:00:05] [Mówca 1]:** Dzień dobry Państwu! Witam na spotkaniu podsumowującym nasze ustalenia kontraktowe.
* **[00:00:18] [Mówca 2]:** Dzień dobry! Przeanalizowaliśmy ofertę. Model biznesowy nam odpowiada, ale musimy doprecyzować warunki płatności i zwrotu z inwestycji.
* **[00:00:35] [Mówca 1]:** Rozumiem. Według naszych wyliczeń efekt oszczędności rzędu 150 tys. PLN rocznie nastąpi już w 14. miesiącu eksploatacji.
* **[00:01:02] [Mówca 3]:** Pod kątem technicznym nasz zespół IT jest gotowy. Podłączenie API zajmie maksymalnie 10 dni roboczych.
* **[00:01:25] [Mówca 2]:** Świetnie. Zależy nam jednak na 30-dniowym terminie płatności faktury po dostarczeniu pierwszej partii.
* **[00:01:48] [Mówca 1]:** Akceptujemy 30 dni odroczenia przy wpłacie 20% kaucji gwarancyjnej w momencie podpisania zamówienia.
* **[00:02:10] [Mówca 2]:** Zgadzamy się na te warunki. Proszę o wpisanie tego do protokołu i przesłanie umowy w poniedziałek.
* **[00:02:28] [Mówca 3]:** Do poniedziałku prześlę także specyfikację portów i środowiska stagingowego.
* **[00:02:40] [Mówca 1]:** Dziękuję za owocną rozmowę, oficjalny protokół trafi do Państwa mailowo.`;

const DEMO_TRANSCRIPT_EN = `# 📋 MINUTES OF MEETING & NEGOTIATION TRANSCRIPT

## 1. General Information & Executive Summary
* **Date & Time:** August 28, 2026, 11:30 AM
* **Meeting Topic:** Strategic negotiations on enterprise B2B workflow automation & energy efficiency
* **Audio Duration:** 00:08:45
* **Identified Speakers:** 3 participants
* **Final Status:** Agreement reached in principle; action items & deliverables assigned
* **AI Transcription Engine:** Gemini 3.5 Transcribe (Live & Diarization)

### 📌 Executive Summary
During the executive meeting, stakeholders agreed upon the commercial contract terms for energy storage equipment supply and B2B automation. [Speaker 1] presented ROI projections and 24/7 SLA warranties. [Speaker 2] raised cash-flow requirements and requested a 30-day payment term. [Speaker 3] verified infrastructure readiness and agreed to complete API integration within 10 business days. Parties resolved commercial terms by granting 30-day net payment with a 20% security deposit.

---

## 2. Action Items & Decisions Matrix

| No. | Decision / Action Item | Owner | Deadline | Status |
|-----|------------------------|-------|----------|--------|
| 1 | Prepare revised Contract draft with 30-day payment terms & 20% deposit | [Speaker 1] | Sep 02, 2026 | 🟡 In Progress |
| 2 | Provide server & API integration technical specifications | [Speaker 3] | Sep 03, 2026 | 🟡 In Progress |
| 3 | Reconcile final budget matrix with Corporate Finance | [Speaker 2] | Sep 04, 2026 | ⏳ Pending |
| 4 | Execute digital signing (QES / Qualified Electronic Signature) | All Parties | Sep 07, 2026 | ⏳ Scheduled |

---

## 3. Objections, Risks & Resolutions Analysis
* **Objection 1 (ROI Horizon):** Client questioned the 24-month payback model. *Resolution:* Presented guaranteed Net Billing savings audit.
* **Objection 2 (Capital Outlay):** Requested a 10% upfront discount. *Resolution:* Replaced with 30-day interest-free deferred payment.
* **Technical Risk:** Possible ERP/WMS integration bottlenecks. *Resolution:* Assigned a dedicated vendor solutions engineer.

---

## 4. Chronological Verbatim Transcript

* **[00:00:05] [Speaker 1]:** Good morning everyone! Welcome to our final negotiation round. Today we aim to finalize terms and delivery schedules.
* **[00:00:18] [Speaker 2]:** Good morning! We have reviewed your proposal thoroughly. The core offering fits our roadmap, but we need flexibility on payment terms.
* **[00:00:35] [Speaker 1]:** Understood. Our ROI financial model shows full cost recovery within 14 months, delivering sustained OPEX reductions.
* **[00:01:02] [Speaker 3]:** From the engineering side, our team is ready. We can connect the REST API in about 10 working days.
* **[00:01:25] [Speaker 2]:** That timeline works well. However, we request net 30 payment terms following initial hardware delivery.
* **[00:01:48] [Speaker 1]:** We can accept net 30 terms subject to a 20% advance security deposit upon order execution.
* **[00:02:10] [Speaker 2]:** That structure is mutually beneficial. Let's record this in the protocol and prepare the contract for Monday.
* **[00:02:28] [Speaker 3]:** I will send over the server configuration files by Monday morning.
* **[00:02:40] [Speaker 1]:** Excellent. Thank you all for a productive session. Meeting minutes will be distributed shortly.`;

export default function MeetingTranscriberView({
  language,
  onBack,
  onGoHome,
  fromCenter = 'office',
  currentUserEmail,
  useEnterpriseContext,
  enterpriseName
}: MeetingTranscriberViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  // Input states - initialized CLEAN by default
  const [activeInputTab, setActiveInputTab] = useState<'mic' | 'upload'>('mic');
  const [meetingTopic, setMeetingTopic] = useState('');
  const [expectedLanguage, setExpectedLanguage] = useState(language);
  const [outputFormat, setOutputFormat] = useState<'mom' | 'verbatim' | 'summary' | 'srt'>('mom');
  const [industryContext, setIndustryContext] = useState('');

  // Sync language selection when parent language changes
  useEffect(() => {
    setExpectedLanguage(language);
  }, [language]);

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordedMimeTypeRef = useRef<string>('audio/webm');
  const timerRef = useRef<any>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const volumeIntervalRef = useRef<any>(null);

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Processing & Results
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [transcriptResult, setTranscriptResult] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);

  // Speaker diarization state
  const [speakers, setSpeakers] = useState<SpeakerMapping[]>([]);

  // Auto-download result once completed
  useEffect(() => {
    if (transcriptResult && typeof transcriptResult === 'string' && transcriptResult.trim().length > 0 && !transcriptResult.startsWith('⚠️ ПОМИЛКА')) {
      const defaultDocTitle = isPl ? 'Protokół i Transkrypcja Spotkania' : isEn ? 'Meeting Minutes & Transcript' : 'Стенограма & Протокол Наради';
      autoDownloadReport(transcriptResult, defaultDocTitle, 'meeting-transcriber');
    }
  }, [transcriptResult, isPl, isEn]);

  // Timer effect for microphone recording
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording, isPaused]);

  // Cleanup audio tracks on unmount
  useEffect(() => {
    return () => {
      if (volumeIntervalRef.current) clearInterval(volumeIntervalRef.current);
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Format time display (00:00 or 00:00:00)
  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper to find cross-browser supported media type
  const getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/aac',
      'audio/ogg;codecs=opus',
      'audio/wav'
    ];
    for (const type of types) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return '';
  };

  // Dynamic 30-minute block pricing calculation (1 block = 30 mins)
  const calculate30MinBlocks = (seconds: number) => {
    if (seconds <= 0) return 1;
    return Math.max(1, Math.ceil(seconds / 1800)); // 1800 seconds = 30 minutes
  };

  const currentSeconds = activeInputTab === 'mic' 
    ? recordingTime 
    : (selectedFile ? Math.max(300, Math.round(selectedFile.size / 15000)) : 525);
  
  const blocksCount = calculate30MinBlocks(currentSeconds);

  // Start Mic Recording
  const startRecording = async () => {
    setErrorMsg(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      streamRef.current = stream;
      audioChunksRef.current = [];

      const supportedMime = getSupportedMimeType();
      const options = supportedMime ? { mimeType: supportedMime } : undefined;
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      recordedMimeTypeRef.current = mediaRecorder.mimeType || supportedMime || 'audio/webm';

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        try {
          const audioCtx = new AudioContextClass();
          audioCtxRef.current = audioCtx;
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 64;
          source.connect(analyser);
          analyserRef.current = analyser;

          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          if (volumeIntervalRef.current) clearInterval(volumeIntervalRef.current);
          volumeIntervalRef.current = setInterval(() => {
            if (analyserRef.current) {
              analyserRef.current.getByteFrequencyData(dataArray);
              let sum = 0;
              for (let i = 0; i < dataArray.length; i++) {
                sum += dataArray[i];
              }
              const avg = sum / dataArray.length;
              setAudioLevel(avg);
            }
          }, 100);
        } catch (e) {
          console.warn('AudioContext setup warning:', e);
        }
      }

      mediaRecorder.start(250);
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      setAudioBlob(null);
    } catch (err: any) {
      console.error('Microphone access error:', err);
      setErrorMsg(isPl 
        ? 'Nie udało się uzyskać dostępu do mikrofonu. Sprawdź uprawnienia w przeglądarce.'
        : isEn
        ? 'Failed to access microphone. Please check browser permissions.'
        : 'Не вдалося отримати доступ до мікрофона. Перевірте дозволи у браузері.');
    }
  };

  // Pause Mic Recording
  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
      }
    }
  };

  // Stop Mic Recording
  const stopRecording = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (volumeIntervalRef.current) clearInterval(volumeIntervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      setAudioLevel(0);

      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.onstop = () => {
          const finalMime = recordedMimeTypeRef.current || mediaRecorderRef.current?.mimeType || 'audio/webm';
          const blob = new Blob(audioChunksRef.current, { type: finalMime });
          setAudioBlob(blob);

          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          setIsRecording(false);
          setIsPaused(false);
          resolve(blob);
        };
        mediaRecorderRef.current.stop();
      } else {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        setIsRecording(false);
        setIsPaused(false);
        resolve(audioBlob);
      }
    });
  };

  // Handle File Selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setErrorMsg(null);
    }
  };

  // Extract speakers from markdown text
  const extractDetectedSpeakers = (mdText: string): string[] => {
    const matchesUk = mdText.match(/\[Спікер \d+\]/g) || [];
    const matchesPl = mdText.match(/\[Mówca \d+\]/g) || [];
    const matchesEn = mdText.match(/\[Speaker \d+\]/g) || [];
    const all = [...matchesUk, ...matchesPl, ...matchesEn];
    const unique = Array.from(new Set(all));
    return unique.map(s => s.replace('[', '').replace(']', ''));
  };

  // Run Transcription Process
  const handleTranscribe = async () => {
    setErrorMsg(null);
    let activeBlob: Blob | null = audioBlob;

    if (activeInputTab === 'mic') {
      if (isRecording) {
        activeBlob = await stopRecording();
      }
      if (!activeBlob || activeBlob.size === 0) {
        alert(isPl ? 'Proszę najpierw włączyć mikrofon i nagrać wypowiedź.' : isEn ? 'Please record audio using the microphone first.' : 'Будь ласка, спочатку увімкніть мікрофон та запишіть аудіо.');
        return;
      }
      if (activeBlob.size < 300) {
        alert(isPl ? 'Nagranie z mikrofonu jest zbyt krótkie (poniżej 300 bajtów).' : isEn ? 'Microphone recording is too short or empty.' : 'Запис з мікрофона надто короткий (менше 300 байт). Спробуйте промовити довшу фразу.');
        return;
      }
    }

    if (activeInputTab === 'upload' && !selectedFile) {
      alert(isPl ? 'Proszę wybrać plik audio lub wideo.' : isEn ? 'Please select an audio or video file.' : 'Будь ласка, виберіть аудіо або відео файл.');
      return;
    }

    setIsTranscribing(true);
    setProgressStep(1);

    const stepInterval = setInterval(() => {
      setProgressStep(prev => (prev < 4 ? prev + 1 : prev));
    }, 1200);

    try {
      const formData = new FormData();
      if (activeInputTab === 'upload' && selectedFile) {
        formData.append('file', selectedFile);
      } else if (activeBlob) {
        const mime = activeBlob.type || recordedMimeTypeRef.current || 'audio/webm';
        const ext = mime.includes('mp4') || mime.includes('aac') ? 'm4a' 
                  : mime.includes('ogg') ? 'ogg' 
                  : mime.includes('wav') ? 'wav' 
                  : 'webm';
        formData.append('file', activeBlob, `mic_recording.${ext}`);
      }

      formData.append('meetingTopic', meetingTopic || (isPl ? 'Spotkanie biznesowe i ustalenia projektowe' : isEn ? 'Executive Business & Project Meeting' : 'Ділові переговори та робоча нарада'));
      formData.append('language', expectedLanguage);
      formData.append('outputFormat', outputFormat);
      formData.append('industryContext', industryContext || (isPl ? 'Standardy biznesowe i protokoły KSH' : isEn ? 'General enterprise & legal standards' : 'ДСТУ 4163:2020 та діловодство'));
      formData.append('blocksCount', blocksCount.toString());

      const res = await fetch('/api/transcribe-audio', {
        method: 'POST',
        body: formData
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || (isPl ? 'Błąd serwera podczas transkrypcji' : isEn ? 'Server error during transcription' : 'Помилка сервера при розпізнаванні аудіо'));
      }

      if (!data.transcript || !data.transcript.trim()) {
        throw new Error(isPl ? 'Nie wykryto mowy w przesłanym pliku.' : isEn ? 'No speech detected in audio.' : 'ШІ не виявив розпізнаного мовлення в наданому аудіофайлі.');
      }

      clearInterval(stepInterval);
      setTranscriptResult(data.transcript);

      const detected = extractDetectedSpeakers(data.transcript);
      if (detected.length > 0) {
        setSpeakers(detected.map((spk, idx) => ({
          original: spk,
          custom: idx === 0 
            ? (isPl ? 'Mówca 1 (Prezes/Dyrektor)' : isEn ? 'Speaker 1 (Executive/Chair)' : 'Спікер 1 (Керівник/CFO)')
            : idx === 1 
            ? (isPl ? 'Mówca 2 (Klient/Partner)' : isEn ? 'Speaker 2 (Client/Partner)' : 'Спікер 2 (Замовник/Партнер)')
            : `${spk} (${isPl ? 'Uczestnik' : isEn ? 'Participant' : 'Учасник'} ${idx + 1})`
        })));
      }

    } catch (err: any) {
      console.error('Transcription error:', err);
      clearInterval(stepInterval);
      setErrorMsg(err.message || (isPl ? 'Wystąpił błąd podczas przetwarzania audio.' : isEn ? 'An error occurred during audio processing.' : 'Виникла помилка під час обробки та розпізнавання аудіо.'));
    } finally {
      setIsTranscribing(false);
    }
  };

  // 1-Click Demo Fill
  const handleFillDemo = () => {
    setErrorMsg(null);
    if (isPl) {
      setMeetingTopic('Negocjacje wdrożenia automatyzacji B2B i efektywności energetycznej');
      setIndustryContext('Umowy B2B, KSH, efektywność energetyczna, Net Billing');
      setTranscriptResult(DEMO_TRANSCRIPT_PL);
      setSpeakers([
        { original: 'Mówca 1', custom: 'Michał Kowalski (Dyrektor Handlowy)' },
        { original: 'Mówca 2', custom: 'Anna Nowak (Dyrektor Finansowy)' },
        { original: 'Mówca 3', custom: 'Piotr Wiśniewski (Kierownik IT)' }
      ]);
    } else if (isEn) {
      setMeetingTopic('Strategic enterprise B2B workflow automation & energy efficiency meeting');
      setIndustryContext('Enterprise SaaS, SLA contracts, Net Billing energy, REST API integration');
      setTranscriptResult(DEMO_TRANSCRIPT_EN);
      setSpeakers([
        { original: 'Speaker 1', custom: 'Alexander (Commercial VP)' },
        { original: 'Speaker 2', custom: 'Sarah (Finance Director)' },
        { original: 'Speaker 3', custom: 'David (Head of Engineering)' }
      ]);
    } else {
      setMeetingTopic('Стратегічні переговори щодо впровадження B2B автоматизації та енергозбереження');
      setIndustryContext('Договори поставки, ДСТУ 4163:2020, Net Billing, REST API інтеграція');
      setTranscriptResult(DEMO_TRANSCRIPT_UK);
      setSpeakers([
        { original: 'Спікер 1', custom: 'Олександр (CFO)' },
        { original: 'Спікер 2', custom: 'Марія (Керівник проектів)' },
        { original: 'Спікер 3', custom: 'Ігор (Техдиректор)' }
      ]);
    }
  };

  // Update Speaker Name dynamically in output display
  const getRenderedTranscript = () => {
    if (!transcriptResult) return '';
    let updated = transcriptResult;
    speakers.forEach(spk => {
      if (spk.custom && spk.custom.trim()) {
        const regex = new RegExp(`\\[${spk.original}\\]`, 'g');
        updated = updated.replace(regex, `**${spk.custom}**`);
      }
    });
    return updated;
  };

  // Speaker Name input change
  const handleSpeakerNameChange = (index: number, newCustomName: string) => {
    setSpeakers(prev => {
      const next = [...prev];
      next[index] = { ...next[index], custom: newCustomName };
      return next;
    });
  };

  // Copy transcript
  const handleCopy = () => {
    if (!transcriptResult) return;
    navigator.clipboard.writeText(getRenderedTranscript());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Download Docx
  const handleDownloadDocx = async () => {
    if (!transcriptResult) return;
    setDownloadingDocx(true);
    try {
      const docTitle = isPl ? 'Protokol_Spotkania_Transkrypcja' : isEn ? 'Meeting_Minutes_Transcript' : 'Протокол_Наради_Стенограма';
      const authorTitle = isPl 
        ? 'Deep Research Hub — Gemini 3.5 Transcribe'
        : isEn 
        ? 'Deep Research Hub — Gemini 3.5 Transcribe'
        : 'Deep Research Hub — ШІ-Стенографіст Gemini 3.5 Transcribe';

      const res = await fetch('/api/convert-md-to-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          markdownText: getRenderedTranscript(),
          title: isPl ? 'Protokół Spotkania i Transkrypcja Negocjacji' : isEn ? 'Meeting Minutes & Negotiation Transcript' : 'Протокол Наради та Стенограма Переговорів',
          author: authorTitle
        })
      });

      if (!res.ok) throw new Error('Conversion failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${docTitle}_${new Date().toISOString().slice(0, 10)}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Docx download error:', err);
      alert(isPl ? 'Błąd generowania dokumentu Word.' : isEn ? 'Failed to generate Word document.' : 'Помилка формування Word документа.');
    } finally {
      setDownloadingDocx(false);
    }
  };

  // Download Plain Text (.txt)
  const handleDownloadTxt = () => {
    if (!transcriptResult) return;
    const blob = new Blob([getRenderedTranscript()], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Transcript_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Dynamic back navigation label
  const backLabel = fromCenter === 'business' 
    ? (isPl ? '← Powrót do Centrum Biznesu' : isEn ? '← Back to Business Hub' : '← Назад до Центру Бізнесу')
    : fromCenter === 'admin'
    ? (isPl ? '← Powrót do Centrum Admina' : isEn ? '← Back to Admin Center' : '← Назад до Центру Адміністрування')
    : (isPl ? '← Powrót do Centrum Biura' : isEn ? '← Back to Office Hub' : '← Назад до Офісного Хабу');

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono max-w-6xl mx-auto">
      
      {/* Header Navigation Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{backLabel}</span>
          </button>

          {onGoHome && (
            <button
              onClick={onGoHome}
              className="px-3.5 py-2 bg-slate-800/80 hover:bg-slate-700 text-slate-300 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
            >
              <Home className="w-4 h-4 text-emerald-400" />
              <span>{isPl ? '🏠 Strona Główna' : isEn ? '🏠 Home' : '🏠 На головну'}</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-3 py-1 text-[11px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md flex items-center gap-1.5 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
            ✨ Gemini 3.5 Transcribe
          </span>
          <span className="px-3 py-1 text-[11px] font-extrabold uppercase bg-cyan-500/20 text-cyan-300 border border-cyan-400/30 rounded-md flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-cyan-400" />
            {isPl ? '🔊 Smart Diaryzacja Mówców' : isEn ? '🔊 Smart Diarization' : '🔊 Смарт-Діаризація'}
          </span>
          <span className="px-3 py-1 text-[11px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded-md flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-amber-400" />
            {isPl ? '⏱️ 19 PLN / 30 min' : isEn ? '⏱️ €9 / 30 min' : '⏱️ 100 грн / 30 хв'}
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950/80 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-emerald-300 shadow-md">
                <Mic className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-white tracking-wide">
                  {isPl 
                    ? 'Transkrybator Audio & Protokoły Spotkań (Gemini 3.5 Transcribe)'
                    : isEn 
                    ? 'Live Audio & Meeting Minutes Transcriber (Gemini 3.5 Transcribe)'
                    : 'Стенографіст & Онлайн-Транскрибатор Зустрічей та Дзвінків'}
                </h1>
                <p className="text-xs sm:text-sm text-slate-300 mt-1">
                  {isPl 
                    ? 'Rozpoznawanie mowy w czasie rzeczywistym z mikrofonu lub plików audio/video. Automatyczna diaryzacja mówców, usuwanie szumów i protokół MOM.'
                    : isEn 
                    ? 'Real-time live microphone streaming and audio/video file transcription. Features automated speaker diarization, noise suppression & MOM protocol.'
                    : 'Розпізнавання голосу через мікрофон у реальному часі або з файлів MP3/WAV/WebM/MP4. Спектральна діаризація спікерів та протоколи ДСТУ 4163:2020.'}
                </p>
              </div>
            </div>

            <button
              onClick={handleFillDemo}
              className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-black uppercase rounded-xl shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-2 cursor-pointer border border-emerald-300 active:scale-95"
            >
              <Sparkles className="w-4 h-4 fill-slate-950" />
              <span>{isPl ? '⚡ Wypełnij demo w 1 klik' : isEn ? '⚡ 1-Click Demo Preset' : '⚡ Заповнити демо-прикладом у 1 клік'}</span>
            </button>
          </div>

          {/* Pricing & Standards Badges */}
          <div className="flex items-center gap-4 text-xs pt-3 border-t border-emerald-500/20 flex-wrap">
            <span className="text-emerald-300 font-bold flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              {isPl ? '🎁 1. uruchomienie BEZPŁATNIE (30 min FREE)' : isEn ? '🎁 1st Run FREE (30 mins FREE)' : '🎁 1-й запуск БЕЗКОШТОВНО (перші 30 хв FREE)'}
            </span>
            <span className="text-slate-400">•</span>
            <span className="text-slate-300">
              {isPl ? 'Model:' : isEn ? 'Engine:' : 'Ядро:'} <strong className="text-emerald-300">Gemini 3.5 Transcribe (Live & Batch)</strong>
            </span>
            <span className="text-slate-400">•</span>
            <span className="text-slate-300">
              {isPl ? 'Formaty:' : isEn ? 'Standards:' : 'Стандарти:'} <strong className="text-white">ДСТУ 4163:2020 / PZP / ISO / SRT</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Main Input Controls Card */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-6 shadow-xl">
        
        {/* Mode Selector Tabs (Live Mic vs File Upload) */}
        <div className="flex items-center gap-2 p-1.5 bg-slate-950 border border-slate-800 rounded-xl">
          <button
            onClick={() => setActiveInputTab('mic')}
            className={`flex-1 py-3 px-4 rounded-lg text-xs font-bold uppercase transition-all flex items-center justify-center gap-2 cursor-pointer ${
              activeInputTab === 'mic'
                ? 'bg-emerald-500 text-slate-950 shadow-md font-extrabold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <Mic className="w-4 h-4" />
            <span>{isPl ? '🎙️ Nagrywanie z mikrofonu (Live Stream)' : isEn ? '🎙️ Live Microphone Recording' : '🎙️ Запис з мікрофона (Live Stream)'}</span>
          </button>
          <button
            onClick={() => setActiveInputTab('upload')}
            className={`flex-1 py-3 px-4 rounded-lg text-xs font-bold uppercase transition-all flex items-center justify-center gap-2 cursor-pointer ${
              activeInputTab === 'upload'
                ? 'bg-emerald-500 text-slate-950 shadow-md font-extrabold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <Upload className="w-4 h-4" />
            <span>{isPl ? '📁 Prześlij plik audio / wideo' : isEn ? '📁 Upload Audio / Video File' : '📁 Завантажити аудіо / відео файл'}</span>
          </button>
        </div>

        {/* Dynamic Input Body */}
        {activeInputTab === 'mic' ? (
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 text-center space-y-6">
            
            {/* Live Audio Visualizer / Timer Display */}
            <div className="space-y-3">
              <div className="inline-flex items-center gap-3 px-4 py-2 bg-slate-900 border border-slate-800 rounded-full text-slate-300 text-xs font-bold flex-wrap justify-center">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-400" />
                  <span>{isPl ? 'Czas nagrania:' : isEn ? 'Duration:' : 'Тривалість:'}</span>
                  <span className="text-lg font-black text-emerald-400 font-mono tracking-wider">
                    {formatTime(recordingTime)}
                  </span>
                </div>

                {isRecording && (
                  <div className="flex items-center gap-1.5 pl-3 border-l border-slate-700">
                    {audioLevel > 2 ? (
                      <Volume2 className="w-4 h-4 text-emerald-400 animate-pulse" />
                    ) : (
                      <VolumeX className="w-4 h-4 text-amber-400 animate-bounce" />
                    )}
                    <span className={`text-[11px] font-mono font-bold ${audioLevel > 2 ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {audioLevel > 2 
                        ? `${isPl ? 'Poziom dźwięku:' : isEn ? 'Audio Level:' : 'Рівень звуку:'} ${Math.round((audioLevel / 255) * 100)}%` 
                        : (isPl ? 'Brak sygnału (0%)' : isEn ? 'Mic Silent (0%)' : 'Мікрофон мовчить (0%)')}
                    </span>
                  </div>
                )}
              </div>

              {/* Animated Waveform Visualizer based on REAL audio input levels */}
              <div className="h-16 flex items-center justify-center gap-1.5 py-2">
                {[...Array(24)].map((_, i) => {
                  const baseHeight = audioLevel > 2 
                    ? Math.min(60, Math.max(10, (audioLevel / 255) * 55 + Math.sin(i + recordingTime) * 15))
                    : 8;
                  return (
                    <div
                      key={i}
                      className={`w-1.5 rounded-full transition-all duration-150 ${
                        isRecording && !isPaused
                          ? audioLevel > 2 ? 'bg-emerald-400' : 'bg-amber-500/60'
                          : 'bg-slate-800'
                      }`}
                      style={{
                        height: isRecording && !isPaused ? `${baseHeight}px` : '8px'
                      }}
                    />
                  );
                })}
              </div>

              {/* Warning if mic is completely quiet */}
              {isRecording && !isPaused && recordingTime > 2 && audioLevel <= 2 && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-300 text-xs font-bold flex items-center justify-center gap-2 max-w-md mx-auto animate-fade-in">
                  <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span>{isPl ? '⚠️ Nie wykryto sygnału mikrofonu (0%). Mów do mikrofonu lub sprawdź uprawnienia.' : isEn ? '⚠️ No audio signal detected (0%). Please speak into the mic or check permissions.' : '⚠️ Звуковий сигнал з мікрофона не виявлено (0%). Говоріть у мікрофон або перевірте дозволи.'}</span>
                </div>
              )}
            </div>

            {/* Microphone Action Buttons */}
            <div className="flex items-center justify-center gap-4 flex-wrap">
              {!isRecording ? (
                <button
                  onClick={startRecording}
                  className="px-6 py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-extrabold text-xs uppercase rounded-xl shadow-lg shadow-rose-600/30 transition-all flex items-center gap-2.5 cursor-pointer border border-rose-400 animate-pulse active:scale-95"
                >
                  <Mic className="w-5 h-5" />
                  <span>{isPl ? 'Rozpocznij nagrywanie na żywo' : isEn ? 'Start Live Recording' : 'Почати запис з мікрофона'}</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={pauseRecording}
                    className="px-5 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-amber-300"
                  >
                    {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    <span>{isPaused ? (isPl ? 'Wznów' : isEn ? 'Resume' : 'Продовжити') : (isPl ? 'Pauza' : isEn ? 'Pause' : 'Пауза')}</span>
                  </button>

                  <button
                    onClick={stopRecording}
                    className="px-5 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-extrabold text-xs uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-slate-600"
                  >
                    <Square className="w-4 h-4 text-rose-400" />
                    <span>{isPl ? 'Zakończ nagrywanie' : isEn ? 'Stop Recording' : 'Завершити запис'}</span>
                  </button>
                </>
              )}
            </div>

            {audioBlob && !isRecording && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-300 text-xs font-bold flex items-center justify-center gap-2 animate-fade-in">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>{isPl ? `Nagranie zakończone (${formatTime(recordingTime)}, ${(audioBlob.size / 1024).toFixed(1)} KB). Gotowe do transkrypcji.` : isEn ? `Recording saved (${formatTime(recordingTime)}, ${(audioBlob.size / 1024).toFixed(1)} KB). Ready for AI processing.` : `Запис завершено успішно (${formatTime(recordingTime)}, ${(audioBlob.size / 1024).toFixed(1)} KB). Готово до розпізнавання ШІ.`}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 text-center space-y-4">
            <div className="border-2 border-dashed border-slate-700 hover:border-emerald-500/50 rounded-xl p-8 transition-colors cursor-pointer relative bg-slate-900/50">
              <input
                type="file"
                accept="audio/*,video/*,.mp3,.wav,.m4a,.webm,.ogg,.mp4,.flac,.aac"
                onChange={handleFileChange}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              />
              <div className="space-y-3 pointer-events-none">
                <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-emerald-400 mx-auto">
                  <FileAudio className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-200">
                    {selectedFile ? selectedFile.name : (isPl ? 'Kliknij lub przeciągnij plik audio/wideo tutaj' : isEn ? 'Click or drag & drop audio/video file here' : 'Натисніть або перетягніть аудіо/відео файл сюди')}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {isPl ? 'Obsługiwane formaty: MP3, WAV, M4A, WebM, OGG, MP4, FLAC (do 500 MB)' : isEn ? 'Supported formats: MP3, WAV, M4A, WebM, OGG, MP4, FLAC (up to 500 MB)' : 'Підтримуються формати: MP3, WAV, M4A, WebM, OGG, MP4, FLAC (до 500 МБ)'}
                  </p>
                </div>
              </div>
            </div>

            {selectedFile && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-300 text-xs font-bold flex items-center justify-between animate-fade-in">
                <span className="flex items-center gap-2 truncate">
                  <FileAudio className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="truncate">{selectedFile.name}</span>
                  <span className="text-slate-400">({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                </span>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-slate-400 hover:text-rose-400 transition-colors p-1 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error Banner */}
        {errorMsg && (
          <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-300 text-xs font-bold space-y-2 animate-fade-in">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
              <span className="text-sm font-black">{isPl ? 'Błąd transkrypcji:' : isEn ? 'Transcription error:' : 'Помилка розпізнавання аудіо:'}</span>
            </div>
            <p className="pl-7 font-sans text-xs text-rose-200">{errorMsg}</p>
          </div>
        )}

        {/* Structured Configuration Parameters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          
          {/* Topic / Context Input */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-bold uppercase text-slate-300 mb-2">
              {isPl ? 'Temat lub kontekst spotkania' : isEn ? 'Meeting Topic & Agenda' : 'Тема або контекст наради / переговорів'}
            </label>
            <input
              type="text"
              value={meetingTopic}
              onChange={(e) => setMeetingTopic(e.target.value)}
              placeholder={isPl ? 'np. Negocjacje umowy B2B wdrożenia oprogramowania CRM' : isEn ? 'e.g. Strategic B2B negotiation on CRM software deployment' : 'Напр. Переговори щодо поставки обладнання та Net Billing'}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* Target Language */}
          <div>
            <label className="block text-xs font-bold uppercase text-slate-300 mb-2">
              {isPl ? 'Główny język wypowiedzi' : isEn ? 'Primary Audio Language' : 'Основна мова спікерів'}
            </label>
            <select
              value={expectedLanguage}
              onChange={(e) => setExpectedLanguage(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-emerald-500 cursor-pointer"
            >
              <option value="uk">🇺🇦 Українська (Авто-визначення термінів)</option>
              <option value="pl">🇵🇱 Polski (Domyślny KSH / B2B)</option>
              <option value="en">🇬🇧 English (International / Bilingual)</option>
            </select>
          </div>

          {/* Industry Context Datalist/Combobox */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-bold uppercase text-slate-300 mb-2">
              {isPl ? 'Specjalizacja branżowa / Słownik terminologii' : isEn ? 'Domain Terminology & Vocabulary' : 'Галузева спеціалізація & Словник термінів'}
            </label>
            <input
              type="text"
              list="industry-presets"
              value={industryContext}
              onChange={(e) => setIndustryContext(e.target.value)}
              placeholder={isPl ? 'Wybierz z listy lub wpisz własne słowa kluczowe...' : isEn ? 'Choose from list or type custom jargon...' : 'Виберіть зі списку або введіть власні терміни...'}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            />
            <datalist id="industry-presets">
              <option value="Ділові переговори, фінанси та комерція (ДСТУ 4163:2020 / KSH / Incoterms)" />
              <option value="Юридичні консультації, адвокатські опитування та судові засідання (ЦКУ, КАСУ, КПКУ)" />
              <option value="Технічні наради, IT-архітектура та розробка (REST API, Cloud, Microservices)" />
              <option value="Будівництво, кошториси та авторський нагляд (АВК-5, ДБН, KNR, FIDIC)" />
              <option value="Медичні консиліуми, фармація та клінічні протоколи (GMP, SmPC, МОЗ)" />
            </datalist>
          </div>

          {/* Output Format Selector */}
          <div>
            <label className="block text-xs font-bold uppercase text-slate-300 mb-2">
              {isPl ? 'Format wyniku końcowego' : isEn ? 'Output Document Format' : 'Формат підсумкового результату'}
            </label>
            <select
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value as any)}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-emerald-500 cursor-pointer"
            >
              <option value="mom">{isPl ? '📋 Protokół MOM + Pełna Stenograma' : isEn ? '📋 MOM Protocol + Full Transcript' : '📋 Протокол MOM за ДСТУ + Стенограма'}</option>
              <option value="verbatim">{isPl ? '📝 Tylko dosłowna stenograma (Verbatim)' : isEn ? '📝 Verbatim Transcript Only' : '📝 Тільки дослівна стенограма з таймкодами'}</option>
              <option value="summary">{isPl ? '📌 Podsumowanie, Zadania & Terminy' : isEn ? '📌 Summary & Action Items Matrix' : '📌 Резюме, домовленості та Action Items'}</option>
              <option value="srt">{isPl ? '⏱️ Napisy z kodami czasowymi (.SRT)' : isEn ? '⏱️ Subtitles with Timestamps (.SRT)' : '⏱️ Субтитри з точними таймкодами (.SRT)'}</option>
            </select>
          </div>

        </div>

        {/* Transcribe Run Button */}
        <button
          onClick={handleTranscribe}
          disabled={isTranscribing}
          className="w-full py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-slate-950 font-black text-sm uppercase rounded-xl shadow-xl transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 border border-emerald-300 active:scale-[0.99]"
        >
          {isTranscribing ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>{isPl ? 'Gemini 3.5 Transcribe przetwarza audio i rozpoznaje mówców...' : isEn ? 'Gemini 3.5 Transcribe is processing audio & diarizing voices...' : 'ШІ Gemini 3.5 Transcribe обробляє аудіо та розпізнає мовців...'}</span>
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 fill-slate-950" />
              <span>{isPl ? '⚡ Uruchom Gemini 3.5 Transcribe & Diaryzację' : isEn ? '⚡ Launch Gemini 3.5 Transcribe & Diarization' : '⚡ Запустити розпізнавання голосу Gemini 3.5 Transcribe'}</span>
            </>
          )}
        </button>

        {/* Processing Steps Indicator */}
        {isTranscribing && (
          <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-3 animate-fade-in">
            <div className="flex items-center justify-between text-xs font-bold text-slate-300">
              <span>{isPl ? 'Postęp przetwarzania Gemini 3.5:' : isEn ? 'Gemini 3.5 Transcription Progress:' : 'Прогрес розпізнавання Gemini 3.5 Transcribe:'}</span>
              <span className="text-emerald-400 font-mono">{isPl ? 'Krok' : isEn ? 'Step' : 'Крок'} {progressStep} / 4</span>
            </div>
            <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all duration-500"
                style={{ width: `${(progressStep / 4) * 100}%` }}
              />
            </div>
            <p className="text-[11px] text-slate-400 italic text-center">
              {progressStep === 1 && (isPl ? "Przesyłanie strumienia audio i wstępna filtracja zakłóceń..." : isEn ? "Uploading audio stream and spectral noise suppression..." : "Завантаження аудіопотоку та спектральне шумозаглушення...")}
              {progressStep === 2 && (isPl ? "Wielomodalna diaryzacja i rozróżnianie głosów mówców..." : isEn ? "Multimodal speaker diarization and voiceprint matching..." : "Мультимодальна діаризація та спектральне розрізнення мовців...")}
              {progressStep === 3 && (isPl ? "Precyzyjne wyznaczanie znaczników czasu (Word-level timestamps)..." : isEn ? "Generating word-level timestamps and verbatim text..." : "Формування тексту стенограми та прив'язка посекундних таймкодів...")}
              {progressStep === 4 && (isPl ? "Generowanie protokołu MOM, macierzy decyzji i wniosków..." : isEn ? "Synthesizing MOM protocol, decision matrix and executive summary..." : "Складання таблиці рішень за ДСТУ 4163:2020 та підсумкового протоколу...")}
            </p>
          </div>
        )}
      </div>

      {/* Results Section */}
      {transcriptResult && (
        <div className="bg-slate-900/90 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 space-y-6 shadow-2xl animate-fade-in">
          
          {/* Result Header Bar */}
          <div className="flex items-center justify-between gap-4 flex-wrap border-b border-slate-800 pb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              <h2 className="text-lg font-black text-white tracking-wide">
                {isPl ? 'Wyniki Transkrypcji & Protokół Spotkania' : isEn ? 'Transcription Results & Meeting Minutes' : 'Результати Стенограми & Протокол Наради'}
              </h2>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleCopy}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? (isPl ? 'Skopiowano!' : isEn ? 'Copied!' : 'Скопійовано!') : (isPl ? 'Kopiuj' : isEn ? 'Copy' : 'Скопіювати')}</span>
              </button>

              <button
                onClick={handleDownloadTxt}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
              >
                <FileText className="w-4 h-4 text-cyan-400" />
                <span>{isPl ? 'Pobierz .TXT' : isEn ? 'Download .TXT' : 'Завантажити .TXT'}</span>
              </button>

              <button
                onClick={handleDownloadDocx}
                disabled={downloadingDocx}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-black uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-emerald-300 shadow-md disabled:opacity-50"
              >
                {downloadingDocx ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                <span>{isPl ? 'Pobierz Word (.docx)' : isEn ? 'Download Word (.docx)' : 'Завантажити Word (.docx по ДСТУ)'}</span>
              </button>
            </div>
          </div>

          {/* Speaker Diarization Custom Name Panel */}
          {speakers.length > 0 && (
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 sm:p-5 space-y-3">
              <div className="flex items-center gap-2 text-xs font-extrabold uppercase text-cyan-300">
                <UserCheck className="w-4 h-4 text-cyan-400" />
                <span>{isPl ? '🔊 Interaktywna zmiana nazwisk mówców (Speaker Rename):' : isEn ? '🔊 Live Speaker Renaming & Attribution:' : '🔊 Редагування імен спікерів у реальному часі (Speaker Rename):'}</span>
              </div>
              <p className="text-[11px] text-slate-400">
                {isPl 
                  ? 'Wpisz rzeczywiste imiona, nazwiska lub stanowiska uczestników — tekst stenogramu oraz pobierany plik Word zaktualizują się natychmiast.'
                  : isEn 
                  ? 'Enter real participant names or titles — the rendered transcript and Word document will mirror changes in real time.'
                  : 'Змініть загальні назви спікерів на реальні ПІБ чи посади учасників — текст стенограми та завантажуваний Word оновляться миттєво.'}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                {speakers.map((spk, idx) => (
                  <div key={idx} className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase block">
                      {spk.original}
                    </label>
                    <input
                      type="text"
                      value={spk.custom}
                      onChange={(e) => handleSpeakerNameChange(idx, e.target.value)}
                      placeholder={`Напр. Спікер ${idx + 1}`}
                      className="w-full px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-emerald-300 font-bold focus:outline-none focus:border-emerald-400"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Main Output Display Box */}
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 space-y-4 text-slate-200 text-xs leading-relaxed overflow-x-auto">
            <div className="prose prose-invert max-w-none space-y-4">
              {getRenderedTranscript().split('\n').map((line, idx) => {
                if (line.startsWith('# ')) {
                  return <h1 key={idx} className="text-lg font-black text-emerald-400 border-b border-slate-800 pb-2 mt-4">{line.replace('# ', '')}</h1>;
                }
                if (line.startsWith('## ')) {
                  return <h2 key={idx} className="text-sm font-bold text-cyan-300 mt-4 mb-2">{line.replace('## ', '')}</h2>;
                }
                if (line.startsWith('### ')) {
                  return <h3 key={idx} className="text-xs font-bold text-slate-200 mt-3 mb-1">{line.replace('### ', '')}</h3>;
                }
                if (line.startsWith('|')) {
                  return <div key={idx} className="font-mono text-[11px] text-slate-300 my-0.5 whitespace-pre">{line}</div>;
                }
                if (line.includes('**[')) {
                  return (
                    <div key={idx} className="p-2.5 bg-slate-900/60 border-l-2 border-emerald-500 rounded-r-lg my-1.5">
                      <span className="text-slate-300">{line.replaceAll('**', '')}</span>
                    </div>
                  );
                }
                return <p key={idx} className="my-1">{line.replaceAll('**', '')}</p>;
              })}
            </div>
          </div>

        </div>
      )}

    </div>
  );
}
